<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-[9999] flex items-end justify-center" @click.self="close">
      <div class="absolute inset-0 bg-ink/40 backdrop-blur-sm" @click="close" />

      <div class="relative w-full max-w-[430px] max-h-[calc(100dvh-16px)] overflow-y-auto overscroll-contain bg-cream rounded-t-2xl px-5 pt-5 pb-sheet-10 animate-slide-up" ref="dialogRef" role="dialog" tabindex="-1" aria-modal="true" :aria-label="title" :aria-busy="busy" @keydown="onDialogKeydown">
        <!-- Заголовок + крестик -->
        <div class="flex items-center justify-between mb-8" style="font-family: Inter, sans-serif;">
          <span class="text-18 font-semibold text-ink">{{ title }}</span>
          <button @click="close"
                  :disabled="busy" aria-label="Закрыть" class="w-11 h-11 flex items-center justify-center rounded-full text-ink active:bg-line transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <p v-if="description" class="text-14 text-ink/70 mb-4">{{ description }}</p>
        <button v-if="copyLabel" type="button" :disabled="busy" @click="$emit('copy')"
          class="w-full min-h-[44px] mb-5 px-4 py-3 rounded-2xl bg-ink/[0.08] text-ink text-15 text-left active:opacity-70">
          {{ copyLabel }}
        </button>
        <fieldset :disabled="busy" :inert="busy" class="border-0 p-0 m-0 min-w-0">
        <!-- Дата -->
        <div class="flex items-center justify-center gap-3 mb-6" style="font-family: Inter, sans-serif;">
          <div class="flex flex-col items-center">
            <span class="text-14 text-ink/70 mb-2">День</span>
            <WheelColumn aria-label="День" :items="days" v-model="selDay" />
          </div>
          <div class="flex flex-col items-center">
            <span class="text-14 text-ink/70 mb-2">Месяц</span>
            <WheelColumn aria-label="Месяц" :items="months" v-model="selMonth" :formatter="monthFormatter" />
          </div>
          <div v-if="showYear" class="flex flex-col items-center">
            <span class="text-14 text-ink/70 mb-2">Год</span>
            <WheelColumn aria-label="Год" :items="years" v-model="selYear" :formatter="String" />
          </div>
        </div>

        <!-- Разделитель -->
        <div class="h-px bg-line mx-4 mb-6" />

        <!-- Время -->
        <div class="flex items-center justify-center gap-2" :class="showRepeat ? 'mb-6' : 'mb-[48px]'" style="font-family: Inter, sans-serif;">
          <!-- Начало -->
          <div class="flex flex-col items-center">
            <span class="text-14 text-ink/70 mb-2">Начало</span>
            <div class="flex items-center gap-1">
              <WheelColumn aria-label="Час начала" :items="hours" v-model="sH" />
              <span class="text-20 font-semibold text-ink mb-1">:</span>
              <WheelColumn aria-label="Минута начала" :items="minutes" v-model="sM" />
            </div>
          </div>

          <span class="text-24 text-ink/60 mx-3 mt-5">–</span>

          <!-- Конец -->
          <div class="flex flex-col items-center">
            <span class="text-14 text-ink/70 mb-2">Конец</span>
            <div class="flex items-center gap-1">
              <WheelColumn aria-label="Час окончания" :items="hours" v-model="eH" />
              <span class="text-20 font-semibold text-ink mb-1">:</span>
              <WheelColumn aria-label="Минута окончания" :items="minutes" v-model="eM" />
            </div>
          </div>
        </div>

        <!-- Повторить: дублирование на будущие даты. «Каждый день» включает
             будни, «завтра» может совпасть с обоими — пересечения схлопывает
             утилита ruleDates, здесь чекбоксы независимы. Подпись с датой у
             каждого варианта считается от выбранной в колёсах даты: повтор
             привязан к блюду, а не к сегодняшнему дню. Варианты без будущих
             дат (блюдо из прошлого) выключены — сервер такие копии не создаёт,
             и молча проглатывать галочку нечестно. -->
        <template v-if="showRepeat">
          <div class="h-px bg-line mx-4 mb-5" />
          <div class="mb-7" style="font-family: Inter, sans-serif;">
            <p class="text-14 text-ink/70 mb-3">{{ repeatLabel }}</p>
            <button
              v-for="opt in visibleRepeatOptions"
              :key="opt.key"
              :disabled="!dateHints[opt.key].enabled"
              @click="repeatState[opt.key] = !repeatState[opt.key]"
              :aria-pressed="repeatState[opt.key]"
              class="w-full flex items-center gap-3 py-2 text-left"
              :class="dateHints[opt.key].enabled ? '' : 'opacity-40'"
            >
              <span
                class="w-[22px] h-[22px] rounded-md flex items-center justify-center shrink-0 transition-colors"
                :class="repeatState[opt.key] ? 'bg-ink' : 'border border-[#C4B8A8] bg-white'"
              >
                <svg v-if="repeatState[opt.key]" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#FAF3E8" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              </span>
              <span class="text-15 text-ink">{{ opt.label }}</span>
              <span class="text-13 text-[#B0A392] ml-auto shrink-0">{{ dateHints[opt.key].hint }}</span>
            </button>
          </div>
        </template>

        </fieldset>
        <p v-if="error" role="alert" class="text-14 text-ink mb-3">{{ error }}</p>
        <!-- Кнопка Сохранить -->
        <button
          @click="onConfirm"
          :disabled="busy"
          class="w-full py-3.5 rounded-2xl bg-ink text-cream text-15 font-semibold active:scale-[0.98] transition-all"
          style="font-family: Inter, sans-serif;"
        >
          {{ busy ? 'Сохраняем…' : confirmLabel }}
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import WheelColumn from './WheelColumn.vue'
import { optionDateHints } from '../utils/repeatRule.js'

const props = defineProps({
  title: { type: String, default: 'Дата и время приёма' },
  description: { type: String, default: '' },
  copyLabel: { type: String, default: '' },
  confirmLabel: { type: String, default: 'Сохранить' },
  showYear: { type: Boolean, default: false },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
  date:      { type: String, default: null },
  startHour: { type: Number, default: 12 },
  startMin:  { type: Number, default: 0 },
  endHour:   { type: Number, default: 12 },
  endMin:    { type: Number, default: 20 },
  // Секция «Повторить»: дублирование блюда/приёма на будущие даты
  showRepeat: { type: Boolean, default: false },
  repeat:     { type: Object,  default: null },  // {today, tomorrow, weekdays, alldays}
  // Подпись секции: у блюда — «Повторить блюдо», у MealGroup — «Повторить приём пищи»
  repeatLabel: { type: String, default: 'Повторить блюдо' },
})

const emit = defineEmits(['confirm', 'close', 'copy'])
function close() { if (!props.busy) emit('close') }
const dialogRef = ref(null)
let previousFocus
onMounted(async () => {
  previousFocus = document.activeElement
  await nextTick()
  dialogRef.value?.focus()
})
onBeforeUnmount(() => { if (previousFocus?.isConnected) previousFocus.focus() })
function onDialogKeydown(event) {
  if (event.key === 'Escape') { event.stopPropagation(); close(); return }
  if (event.key !== 'Tab') return
  const targets = [...(dialogRef.value?.querySelectorAll('button:not(:disabled), [tabindex="0"]') || [])]
    .filter(el => !el.closest('[inert]') && el.offsetParent !== null)
  if (!targets.length) { event.preventDefault(); return }
  const index = targets.indexOf(document.activeElement)
  if (index < 0 || (event.shiftKey && index === 0) || (!event.shiftKey && index === targets.length - 1)) {
    event.preventDefault()
    targets[event.shiftKey ? targets.length - 1 : 0].focus()
  }
}

// ── Дата ──────────────────────────────────────────────────────────────────────
const initDate = props.date ? new Date(props.date + 'T00:00:00') : new Date()
const initYear = initDate.getFullYear()
const selYear = ref(initYear)
const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i)

const selDay   = ref(initDate.getDate())
const selMonth = ref(initDate.getMonth() + 1)

const days = computed(() => Array.from({ length: new Date(selYear.value, selMonth.value, 0).getDate() }, (_, i) => i + 1))
watch([selYear, selMonth], () => { selDay.value = Math.min(selDay.value, days.value.length) })
const months = Array.from({ length: 12 }, (_, i) => i + 1)

const monthNames = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
const monthFormatter = (v) => monthNames[v - 1]

// ── Время ─────────────────────────────────────────────────────────────────────
const hours   = Array.from({ length: 24 }, (_, i) => i)
const minutes = Array.from({ length: 12 }, (_, i) => i * 5)

const sH = ref(props.startHour)
const sM = ref(props.startMin)
const eH = ref(props.endHour)
const eM = ref(props.endMin)

// ── Повторить ────────────────────────────────────────────────────────────────
// «Сегодня» — только у блюда из прошлого (optionDateHints решает по дате):
// пользователи хотели повторить вчерашний обед сегодня, а попап предлагал
// лишь «завтра».
const repeatOptions = [
  { key: 'today',    label: 'Сегодня' },
  { key: 'tomorrow', label: 'Завтра' },
  { key: 'weekdays', label: 'Каждый будний день' },
  { key: 'alldays',  label: 'Каждый день' },
]
const repeatState = reactive({
  today:    !!props.repeat?.today,
  tomorrow: !!props.repeat?.tomorrow,
  weekdays: !!props.repeat?.weekdays,
  alldays:  !!props.repeat?.alldays,
})

// Дата выбирается колёсами здесь же — подписи и доступность вариантов повтора
// следуют за ней, а не за датой, с которой попап открыли. Для блюда из
// прошлого даты считаются от сегодня — как их посчитает и сохранение.
const selectedDateStr = computed(() => {
  const maxDay = new Date(selYear.value, selMonth.value, 0).getDate()
  const day = Math.min(selDay.value, maxDay)
  return `${selYear.value}-${String(selMonth.value).padStart(2, '0')}-${String(day).padStart(2, '0')}`
})
const dateHints = computed(() => optionDateHints(selectedDateStr.value))
const visibleRepeatOptions = computed(() => repeatOptions.filter(o => dateHints.value[o.key].visible))
// Дату перекрутили на сегодня/будущее — скрытая галочка «сегодня» не должна
// уехать на сервер
watch(() => dateHints.value.today.visible, (v) => { if (!v) repeatState.today = false })

// Ограничение: конец >= начала, разница <= 2ч
function clampEnd() {
  const startTotal = sH.value * 60 + sM.value
  let endTotal = eH.value * 60 + eM.value

  if (endTotal < startTotal) endTotal = startTotal
  if (endTotal - startTotal > 120) endTotal = startTotal + 120

  eH.value = Math.floor(endTotal / 60) % 24
  eM.value = Math.round(endTotal % 60 / 5) * 5
}

watch([sH, sM], clampEnd)
watch([eH, eM], () => {
  const startTotal = sH.value * 60 + sM.value
  let endTotal = eH.value * 60 + eM.value
  if (endTotal < startTotal || endTotal - startTotal > 120) clampEnd()
})

function onConfirm() {
  if (props.busy) return
  clampEnd()

  emit('confirm', {
    // День уже ограничен допустимым диапазоном месяца в selectedDateStr
    date: selectedDateStr.value,
    startHour: sH.value,
    startMin: sM.value,
    endHour: eH.value,
    endMin: eM.value,
    ...(props.showRepeat ? { repeat: { ...repeatState } } : {}),
  })
}
</script>

<style scoped>
.animate-slide-up {
  animation: slideUp 0.25s ease-out;
}
@keyframes slideUp {
  from { transform: translateY(100%); }
  to   { transform: translateY(0); }
}
@media (prefers-reduced-motion: reduce) { .animate-slide-up { animation: none; } }
</style>
